import { CreateAnnotationProperty } from './AnnotationForm.types';
import { Localization, AnnotationProperty, PolygonCoordinate, AnnotationsForGrid, MLRequest } from '../annotation.types';
import {
  AnnotationStandardDetails,
  FindingTypeDetails,
  FindingTypeProperty,
  FindingTypeRuleDetails
} from '../../AnnotationStandardForm/AnnotationStandardForm.slice';
import { ValidationModalDto } from '../AnnotationValidationModal/AnnotationValidationModal.slice';

export const ANNOTATION_TAB_KEY = '1';
export const COMMENTS_TAB_KEY = '2';

export const populateLocalizationProperties = (
  newProperties: CreateAnnotationProperty[][],
  localization: Localization | null,
  selectedFindingType: FindingTypeDetails | null,
  localizationEditable: boolean
) => {
  if (!newProperties || !selectedFindingType) return;
  if (localization && localization.distanceToRoot >= 0) {
    newProperties.forEach(group => {
      group.forEach(property => {
        const representations = selectedFindingType.representations;
        const representation = representations.find(x => x.findingTypePropertyId === property.findingTypePropertyId);

        if (representation) {
          property.required = representation.isMandatory;
        }

        if (representation && representation.group === 'Localization') {
          populateLocalizationProperty(property, localization, localizationEditable);
        }
      });
    });
  } else {
    setupLocalizationPropertiesForUpdate(newProperties, selectedFindingType, localizationEditable);
  }
};

export const setupLocalizationPropertiesForUpdate = (
  newProperties: CreateAnnotationProperty[][],
  selectedFindingType: FindingTypeDetails | null,
  localizationEditable: boolean
) => {
  if (!newProperties || !selectedFindingType) return;
  newProperties.forEach(group => {
    group.forEach(property => {
      const representations = selectedFindingType.representations;
      const representation = representations.find(x => x.findingTypePropertyId === property.findingTypePropertyId);

      if (representation && representation.group === 'Localization') {
        property.isReadonly = !localizationEditable;
        property.required = false;
      }
    });
  });
};

const populateLocalizationProperty = (property: CreateAnnotationProperty, localization: Localization, localizationEditable: boolean) => {
  localizationPropertyAssignment(property, localization);

  property.isReadonly = !localizationEditable;
  property.required = false;
};

export const localizationPropertyAssignment = (property: CreateAnnotationProperty | AnnotationProperty, localization: Localization) => {
  switch (property.name) {
    case 'DistanceToRootM':
      property.value = localization.distanceToRoot ? localization.distanceToRoot.toString() : '';
      break;

    case 'DistanceToLeadingEdgeM':
      property.value = localization.distanceToLeadingEdge ? localization.distanceToLeadingEdge.toString() : '';
      break;

    case 'HomographyShape':
      property.value = localization.coordinatesOnImage ? getShapeStringFormat(localization.coordinatesOnImage) : '';
      break;

    case 'ShapeOnCanvas':
      property.value = localization.coordinatesOnCanvas ? getShapeStringFormat(localization.coordinatesOnCanvas) : '';
      break;

    case 'WidthMm':
      property.value = localization.width ? localization.width.toString() : '';
      break;

    case 'HeightMm':
      property.value = localization.height ? localization.height.toString() : '';
      break;

    case 'AreaMm2':
      property.value = localization.area ? localization.area.toString() : '';
      break;

    case 'LengthMm':
      property.value = localization.boundingBoxDiagonal ? localization.boundingBoxDiagonal.toString() : '';
      break;

    default:
      break;
  }
};

export const preparePropertyGroupes = (
  annotationStandard: AnnotationStandardDetails | null,
  newProperties: CreateAnnotationProperty[],
  selectedFindingType: FindingTypeDetails | null
) => {
  if (annotationStandard) {
    const commonProperties: CreateAnnotationProperty[] = [];
    const localizationProperties: CreateAnnotationProperty[] = [];

    if (newProperties && annotationStandard && selectedFindingType) {
      newProperties.forEach(prop => {
        const representations = selectedFindingType.representations;
        const representation = representations.find(x => x.findingTypePropertyId === prop.findingTypePropertyId);

        if (representation && representation.group === 'Localization') {
          prop.isReadonly = true;
          localizationProperties.push(prop);
        } else {
          commonProperties.push(prop);
        }
      });

      return [commonProperties, localizationProperties];
    }
  } else {
    return [newProperties];
  }
};

const getShapeStringFormat = (cordArrays: { x: number[]; y: number[] }) => {
  if (cordArrays.x.length === cordArrays.y.length) {
    let shape = '';
    for (let index = 0; index < cordArrays.x.length; index++) {
      shape += `${cordArrays.x[index]},${cordArrays.y[index]}:`;
    }
    return shape;
  }

  return '';
};

export const getMLRequestAndValidationResultForNotUpdatedShape = (record: AnnotationsForGrid) => {
  const validationResult: ValidationModalDto = {
    isShapeValid: true,
    showModal: true,
    overlappingIssue: false,
    outOfBoundariesIssue: false,
    shouldSkipCallToLocalization: true
  };

  const polygonCoordinates: PolygonCoordinate = {
    x: [],
    y: []
  };

  record.shape.forEach(point => {
    polygonCoordinates.x.push(Math.trunc(point.x));
    polygonCoordinates.y.push(Math.trunc(point.y));
  });

  const mlRequest: MLRequest = {
    polygonCoordinates
  };

  return { mlRequest, validationResult };
};

export const getPropertyInitialValue = (
  property: FindingTypeProperty,
  distanceToRoot: string,
  annotationForEdit: AnnotationsForGrid | null
): string => {
  let result = '';

  if (property) {
    if (annotationForEdit && annotationForEdit.properties) {
      const currentProperty = annotationForEdit!.properties.find(x => x.name === property.name);
      if (currentProperty) {
        return currentProperty!.value;
      }
    }

    const isDistanceToRootProp = property.name.includes('DistanceToRootM');
    if (!annotationForEdit && distanceToRoot && isDistanceToRootProp) {
      return distanceToRoot;
    }
  }
  return result;
};

export const propertiesValueInitialization = (properties: CreateAnnotationProperty[][] | undefined, findingTypeChange: boolean) => {
  if (properties) {
    properties
      .flat()
      .filter(x => x.required)
      .forEach((x: CreateAnnotationProperty) => {
        if (findingTypeChange) {
          x.value = '';
        }
      });
  }
};

const anyValueForRule = '';

const filterRulesWithPropertyValue = (findingTypeRuleDetails: FindingTypeRuleDetails, element: CreateAnnotationProperty) => {
  var propertyItems = findingTypeRuleDetails.items.filter(y => y.findingTypePropertyId === element.findingTypePropertyId);
  var filteredPropertyItemsWithSelectedValue = propertyItems.filter(y => y.value === anyValueForRule || y.value === element.value);
  var haveAnyRule = filteredPropertyItemsWithSelectedValue.length > 0;
  return haveAnyRule;
};

export const getPropertyPossibleValues = (
  group: CreateAnnotationProperty[],
  property: CreateAnnotationProperty,
  selectedRules: FindingTypeRuleDetails[]
): string[] => {
  var newRules = selectedRules;

  for (const element of group) {
    if (element === property) {
      break;
    }

    newRules = newRules.filter(x => filterRulesWithPropertyValue(x, element));
  }

  return newRules.map(x => x.items.filter(y => y.findingTypePropertyId === property.findingTypePropertyId).map(y => y.value)).flat();
};

export const prepareSelectInputValues = (
  propertyTemplate: FindingTypeProperty,
  group: CreateAnnotationProperty[],
  property: CreateAnnotationProperty,
  index: number,
  selectedRules: FindingTypeRuleDetails[],
  properties: CreateAnnotationProperty[][],
  updateProperties: (properties: CreateAnnotationProperty[][]) => void
): string[] => {
  let values: string[] = [];
  const allValues = propertyTemplate.values.split(',').map(x => x.trim());
  let possibleValues: string[] = [];

  possibleValues = getPropertyPossibleValues(group, property, selectedRules);

  if (selectedRules.length === 0) {
    possibleValues = [''];
  }

  const showAll = possibleValues.includes('');
  values = showAll ? allValues : [...new Set(possibleValues)];

  if (values.length === 1 && property.value === '') {
    valueUpdated(property.findingTypePropertyId, values[0], updateProperties, properties);
  } else if (index > 0 && group[index - 1].value === '') {
    values = [];
  }

  values.sort((x, y) => {
    return allValues.indexOf(x) - allValues.indexOf(y);
  });

  return values;
};

export const valueUpdated = (
  id: string,
  value: string,
  updateProperties: (properties: CreateAnnotationProperty[][]) => void,
  properties: CreateAnnotationProperty[][]
) => {
  if (value == null) {
    return;
  }

  if (properties) {
    const updatedProperties = [...properties];
    updatedProperties.forEach(group => {
      let updated = false;
      group.forEach(prop => {
        if (updated && updatedProperties.indexOf(group) === 0) {
          prop.value = '';
        }

        if (prop.findingTypePropertyId === id) {
          prop.value = value;
          updated = true;
        }
      });
    });

    updateProperties(updatedProperties);
  }
};

export enum AssessmentAssigneeOption {
  Own = 1,
  SGRE,
  Both
}

export enum AssessmentAssignee {
  Own = 1,
  SGRE
}

export const isAnnotationFormValid = (properties: CreateAnnotationProperty[][] | undefined) => {
  if (!properties) {
    return false;
  }
  return !properties.flat().some((x: CreateAnnotationProperty) => x.required === true && x.value === '');
};

export const scrollToBottomOfAnnotationModal = () => {
  var annotationBottom = document.getElementById('assessment-bottom');

  if (annotationBottom) {
    annotationBottom.scrollIntoView({ behavior: 'smooth' });
  }
};
